/**
 * useSandboxOrgOptions — the curated Sandbox organization list, shared by the
 * homepage tunnel picker (ContextSelector) and the in-app top-bar org dropdown
 * (TunnelOrgChip) so both always offer the exact same choices.
 *
 * The options are curated, NOT the raw org roster: a sandbox org must
 * (a) actually own a portfolio (else picking it scopes to an empty tree) and
 * (b) NOT belong to a demo company — Gentex/Boeing/ConstructCo/Codan are
 * demos, reached via the demo profiles, not the sandbox. Mach12 (the featured
 * platform org) is the one kept catch-all, surfaced first + flagged.
 *
 * Org DISCOVERY must be unscoped: the scoped getPortfolios() in sandbox with
 * no org selected filters to organization_id IS NULL, so it can never see
 * org-owned portfolios (Mach12 incl.) — a chicken-and-egg that once left the
 * homepage picker permanently on "Loading organizations…".
 *
 * Self-disables outside sandbox mode (queries don't fire; options come back
 * empty), so callers can invoke it unconditionally.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as dataService from '@/services/dataService'
import { useAppStore } from '@/store/appStore'
import { DEMO_COMPANIES } from '@/types/demoCompanies'

export function useSandboxOrgOptions(): {
  options: Array<{ id: string; name: string }>
  loading: boolean
} {
  const appMode = useAppStore((s) => s.appMode)
  const { data: sandboxOrgs } = useQuery({
    queryKey: ['sandbox-organizations'],
    queryFn: () => dataService.getOrganizations(),
    enabled: appMode === 'sandbox',
    staleTime: 5 * 60 * 1000,
  })
  const { data: sandboxOrgPortfolios, isLoading } = useQuery({
    queryKey: ['sandbox-portfolio-orgs'],
    queryFn: () => dataService.getPortfoliosForOrgDiscovery(),
    enabled: appMode === 'sandbox',
    staleTime: 5 * 60 * 1000,
  })
  const options = useMemo(() => {
    // Org ids that actually own at least one portfolio (unscoped) — so every
    // option yields a non-empty tree.
    const orgsWithData = new Set(
      ((sandboxOrgPortfolios ?? []) as Array<{ organizationId?: string | null }>)
        .map((p) => p.organizationId)
        .filter((x): x is string => !!x),
    )
    // Demo-company org families to exclude (e.g. org_gtxvs_corp -> 'org_gtxvs',
    // which also catches the value-stream sub-orgs org_gtxvs_aero/drvs/…). The
    // featured platform org (Mach12) is intentionally NOT excluded.
    const demoOrgPrefixes = DEMO_COMPANIES.filter((c) => !c.featured && c.castOrgId).map((c) =>
      c.castOrgId!.replace(/_corp$/, ''),
    )
    const isDemoOrg = (id: string) => demoOrgPrefixes.some((pre) => id === pre || id.startsWith(pre + '_'))
    const rows = (sandboxOrgs ?? []) as Array<{ id?: unknown; name?: unknown }>
    const opts = rows
      .map((o) => ({ id: String(o.id ?? ''), name: String(o.name ?? o.id ?? '') }))
      .filter((o) => o.id && orgsWithData.has(o.id) && !isDemoOrg(o.id))
    // Mach12 (platform org) first, then the rest alphabetically.
    opts.sort((a, b) => {
      if (a.id === 'org_mach12') return -1
      if (b.id === 'org_mach12') return 1
      return a.name.localeCompare(b.name)
    })
    return opts
  }, [sandboxOrgs, sandboxOrgPortfolios])
  return { options, loading: isLoading }
}
